<template>
  <!-- A single color swatch in the generator: the colour (atoms/ColorSwatch),
       then a white info panel with the name, the value and a Copy pill. In edit
       mode a select + delete overlay is shown. Purely presentational: it emits
       events and lets the page own selection/clipboard/deletion logic. -->
  <div
    class="relative h-min overflow-hidden rounded-lg border-4 border-gray-900 bg-white shadow transition"
    :class="{ 'outline-dashed outline-2 outline-offset-0 outline-gray-700': selected && editable }"
    :style="cardStyle"
  >
    <ColorSwatch :color="colorObj.color" />

    <div class="relative bg-white px-4 pb-4 pt-2 sm:px-6">
      <p class="text-xs text-gray-500">{{ colorObj.name }}</p>
      <p class="text-xs font-bold text-gray-600">{{ colorObj.color }}</p>
      <CopyPill class="mt-2" :aria-label="`Copy ${colorObj.color}`" @click.stop="$emit('copy', colorObj.color)" />
    </div>

    <div v-if="editable" class="absolute inset-x-0 top-0 flex justify-between p-2">
      <button
        type="button"
        class="flex items-center gap-2 rounded-full bg-gray-700 px-2 py-1 text-white transition hover:bg-gray-800"
        :aria-pressed="selected"
        @click.stop="$emit('toggle-select')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <rect x="0.5" y="0.5" width="9" height="9" rx="1.5" fill="#626C7A" stroke="white" />
          <path v-if="selected" d="M4.81641 7.59855L4.8164 7.59857C4.77644 7.66162 4.72357 7.71478 4.66144 7.75404C4.59929 7.79331 4.52948 7.81767 4.45704 7.82521C4.3846 7.83275 4.31151 7.82326 4.24309 7.79749C4.17468 7.77172 4.11277 7.73037 4.06172 7.67661L4.06171 7.6766L2.30385 5.82472L2.30264 5.82344L2.30266 5.82342C2.21654 5.72606 2.17003 5.59777 2.17224 5.46567C2.17446 5.33357 2.22524 5.20704 2.31462 5.11288C2.40408 5.01864 2.52531 4.96416 2.65295 4.96178C2.7806 4.95941 2.90364 5.00936 2.99625 5.10028L2.99751 5.10152L2.99749 5.10154L4.33204 6.50748L6.93041 2.40115L6.93044 2.4011C7.00213 2.2881 7.11399 2.20898 7.24198 2.18214C7.37003 2.15529 7.50275 2.1832 7.61079 2.25919L4.81641 7.59855ZM4.81641 7.59855L7.74618 2.96883L7.70393 2.94209L4.81641 7.59855Z" fill="white" stroke="white" stroke-width="0.1" />
        </svg>
        <span class="text-xs font-medium">Select</span>
      </button>
      <button
        type="button"
        class="rounded-full bg-gray-700 p-1 text-white transition hover:bg-gray-800"
        :aria-label="`Delete ${colorObj.color}`"
        @click.stop="$emit('delete')"
      >
        <TrashIcon class="size-4" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { TrashIcon } from '@heroicons/vue/16/solid'
import ColorSwatch from '~/components/atoms/ColorSwatch.vue'
import CopyPill from '~/components/atoms/CopyPill.vue'

defineProps({
  // { color, name, group? }
  colorObj: { type: Object, required: true },
  index: { type: Number, required: true },
  editable: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
})
defineEmits(['copy', 'toggle-select', 'delete'])

// A code palette routinely runs to 150+ of these cards, and they all mount in
// the single click that switches to that tab. `content-visibility: auto` keeps
// the ones outside the viewport out of layout and paint until they're scrolled
// to, which is the larger half of that click's cost. The intrinsic height is
// what a card actually measures (164-167px across breakpoints); `auto` means it
// is only used until a card has been rendered once, after which its real size
// is remembered — so scrolling doesn't shift.
const cardStyle = 'content-visibility: auto; contain-intrinsic-size: auto 166px'
</script>
